import React, { useState, useEffect } from 'react';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import axios from 'axios';
import PropTypes from "prop-types";
import { Table, Badge, Button, Modal, Form, Accordion, Card } from 'react-bootstrap';
import AddCleaning from './PopUps/Add/AddCleaning';
import EditCleaning from './PopUps/Edit/EditCleaning';
import * as Icon from 'react-feather';
import { canPageAction } from '../../config/roleConfig';
import DeactivateConfirmModal from '../Common/DeactivateConfirmModal';

function Cleaning(props)  {
    const authHeader = useAuthHeader();
    const authUser = useAuthUser();
    const currentUser = authUser();
    const canRead = canPageAction(currentUser, 'operations.cleaning', 'read');
    const canWrite = canPageAction(currentUser, 'operations.cleaning', 'write');
    const canDelete = canPageAction(currentUser, 'operations.cleaning', 'delete');

    const [data, setData] = useState([]);
    const [inventoryOptions, setInventoryOptions] = useState([]);
    const [userOptions, setUserOptions] = useState([]);
    const [selectedInventoryId, setSelectedInventoryId] = useState('');
    const [expandedCleaning, setExpandedCleaning] = useState(null);
    const [cleaningToDeactivate, setCleaningToDeactivate] = useState(null);
    const [deactivatingCleaning, setDeactivatingCleaning] = useState(false);
    const apiUrl = process.env.REACT_APP_URL;

    const get_all_data = (inventoryId = '') => {
        let url = apiUrl + '/services/cleaning';
        if (inventoryId) {
            url += `?inventory_id=${inventoryId}`;
        }

        axios({
            method:'GET',
            url: url,
            headers: {
                Authorization: authHeader()
            },
        }).then(response => {
            setData(response.data.msg.Cleanings || []);
        })
        .catch(error => {
            console.error('Error fetching cleaning data:', error);
            alert('Error al cargar datos de limpieza');
        });
    }

    const get_inventory_options = () => {
        axios({
            method: 'GET',
            url: apiUrl + '/units/get_all_units',
            headers: {
                Authorization: authHeader()
            },
        }).then(response => {
            setInventoryOptions(response.data.msg.Units || []);
        })
        .catch(error => {
            console.error('Error fetching inventory options:', error);
        });
    }

    const get_user_options = () => {
        axios({
            method: 'GET',
            url: apiUrl + '/services/users',
            headers: {
                Authorization: authHeader()
            },
        }).then(response => {
            setUserOptions(response.data.msg.Users || []);
        })
        .catch(error => {
            console.error('Error fetching user options:', error);
        });
    }

    useEffect(() => {
        get_inventory_options();
        get_user_options();
    }, []);

    useEffect(() => {
        if (inventoryOptions.length > 0) {
            const firstUnitId = inventoryOptions[0].id;
            setSelectedInventoryId(firstUnitId);
            get_all_data(firstUnitId);
        }
    }, [inventoryOptions]);

    const handleInventoryChange = (e) => {
        const inventoryId = e.target.value;
        setSelectedInventoryId(inventoryId);
        get_all_data(inventoryId);
    };

    const getCompletionBadge = (isCompleted) => {
        return isCompleted ?
            <Badge bg="success">Completado</Badge> :
            <Badge bg="warning">Pendiente</Badge>;
    };

    const toggleCleaningDetails = (cleaningId) => {
        setExpandedCleaning(expandedCleaning === cleaningId ? null : cleaningId);
    };

    const deleteCleaning = (cleaning) => {
        if (!cleaning) {
            return;
        }

        setDeactivatingCleaning(true);
        axios({
            method: 'DELETE',
            url: apiUrl + `/services/cleaning/${cleaning.id}`,
            headers: {
                Authorization: authHeader()
            }
        }).then(() => {
            setCleaningToDeactivate(null);
            refreshData();
        }).catch(error => {
            alert(error.response?.data?.error || error.response?.data?.msg || 'No se pudo desactivar la limpieza');
            console.log(error);
        }).finally(() => {
            setDeactivatingCleaning(false);
        });
    };

    const refreshData = () => {
        get_all_data(selectedInventoryId);
    };

    return (
        <>
        <div className="card mb-4">
            <div className="card-body">
                <div className="card-header d-flex justify-content-between">
                    <h5 className="card-title">Registros de Limpieza</h5>
                    {canWrite && (
                        <AddCleaning
                            setData={refreshData}
                            inventoryOptions={inventoryOptions}
                            userOptions={userOptions}
                        />
                    )}
                </div>

                <div className="mb-3 d-flex align-items-center">
                    <Form.Group className="d-flex align-items-center">
                        <Form.Label className="me-2 mb-0" style={{whiteSpace: 'nowrap'}}>Filtrar por Unidad:</Form.Label>
                        <Form.Select
                            value={selectedInventoryId}
                            onChange={handleInventoryChange}
                            style={{width: '250px'}}
                        >
                            {inventoryOptions.map(inventory => (
                                <option key={inventory.id} value={inventory.id}>
                                    {inventory.inventory_name || inventory.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </div>

                <div>
                    <Table className="m-0" responsive>
                        <thead>
                            <tr>
                                <th>Unidad</th>
                                <th>Fecha/Hora Servicio</th>
                                <th>Responsable</th>
                                <th>Supervisor</th>
                                <th>Estado</th>
                                <th>Costo Total</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map(cleaning => (
                                <React.Fragment key={cleaning.id}>
                                    <tr>
                                        <td>
                                            <div>
                                                <strong>{cleaning.inventory_name}</strong>
                                                {cleaning.inventory_plates && (
                                                    <div className="text-muted small">{cleaning.inventory_plates}</div>
                                                )}
                                            </div>
                                        </td>
                                        <td>{cleaning.service_datetime}</td>
                                        <td>{cleaning.responsible_name || 'No asignado'}</td>
                                        <td>{cleaning.supervisor_name || 'No asignado'}</td>
                                        <td>{getCompletionBadge(cleaning.is_completed)}</td>
                                        <td>${parseFloat(cleaning.total_cost || 0).toFixed(2)}</td>
                                        <td>
                                            <div className="d-flex gap-1">
                                                {canRead && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        onClick={() => toggleCleaningDetails(cleaning.id)}
                                                    >
                                                        <Icon.Eye size={14} />
                                                    </Button>
                                                )}
                                                {canWrite && (
                                                    <EditCleaning
                                                        setData={refreshData}
                                                        cleaningInfo={cleaning}
                                                        inventoryOptions={inventoryOptions}
                                                        userOptions={userOptions}
                                                    />
                                                )}
                                                {canDelete && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline-danger"
                                                        onClick={() => setCleaningToDeactivate(cleaning)}
                                                    >
                                                        <Icon.Trash2 size={14} />
                                                    </Button>
                                                )}
                                                {!canRead && !canWrite && !canDelete && (
                                                    <span className="text-muted">N/A</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {expandedCleaning === cleaning.id && (
                                        <tr>
                                            <td colSpan="7">
                                                <div className="p-3 bg-light">
                                                    <div className="row">
                                                        <div className="col-md-4">
                                                            <p><strong>Unidad:</strong> {cleaning.inventory_name}</p>
                                                            <p><strong>Placas:</strong> {cleaning.inventory_plates || 'N/A'}</p>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <p><strong>Responsable:</strong> {cleaning.responsible_name || 'N/A'}</p>
                                                            <p><strong>Supervisor:</strong> {cleaning.supervisor_name || 'N/A'}</p>
                                                        </div>
                                                        <div className="col-md-4">
                                                            <p><strong>Costo:</strong> ${parseFloat(cleaning.total_cost || 0).toFixed(2)}</p>
                                                            <p><strong>Notas:</strong> {cleaning.notes || 'N/A'}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                    {cleaning.notes && expandedCleaning !== cleaning.id && (
                                        <tr>
                                            <td colSpan="7" className="border-0 pt-0">
                                                <div className="px-3 py-2 bg-light rounded">
                                                    <small className="text-muted">
                                                        <strong>Notas:</strong> {cleaning.notes}
                                                    </small>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                            {data.length === 0 && (
                                <tr>
                                    <td colSpan="7" className="text-center text-muted">
                                        No hay registros de limpieza para esta unidad
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </div>
        </div>
        <DeactivateConfirmModal
            show={Boolean(cleaningToDeactivate)}
            title="Desactivar Limpieza"
            itemLabel={cleaningToDeactivate?.inventory_name}
            confirming={deactivatingCleaning}
            onCancel={() => !deactivatingCleaning && setCleaningToDeactivate(null)}
            onConfirm={() => deleteCleaning(cleaningToDeactivate)}
        />
        </>
    );
}

export default Cleaning;
